// react
import React, { useEffect, useState } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import {
  Box,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, Add as AddIcon } from "@mui/icons-material";

// components
import { Puller } from "components/common/Puller";

// features/databox
import { addViewGroup, addViewGroupReadOnly } from "features/databox/manageViewGroup";
import getViewGroups from "features/databox/getViewGroups";
import getViewGroupsReadOnly from "features/databox/getViewGroupsReadOnly";

// features/group
import getUserGroups from "features/group/getUserGroups";

// A ---------------------------------------------------------------------- M

const AddViewGroupDialog = ({ tag, open, setOpen, handleSuccessfulAdded, handleErrorAdded }) => {
  const { user, conservSostL1 } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [permissions, setPermissions] = useState("r");
  const [groupAlreadyAdded, setGroupAlreadyAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  useEffect(() => {
    if (open) {
      const fetchGroups = async () => {
        setIsLoading(true);

        try {
          const userGroups = await getUserGroups(user.uid);
          const viewGroups = await getViewGroups(tag.id);
          const viewGroupReadOnly = await getViewGroupsReadOnly(tag.id);
          const viewGroupIds = viewGroups.map((group) => group.id);
          const viewGroupReadOnlyIds = viewGroupReadOnly.map((group) => group.id);
          const filteredGroups = userGroups.filter((group) => !viewGroupIds.includes(group.id) && !viewGroupReadOnlyIds.includes(group.id));
          filteredGroups.sort((a, b) => a.name.localeCompare(b.name));
          setGroups(filteredGroups);
        } catch (error) {
          console.error("Error fetching groups:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchGroups();
    }
  }, [tag, open, user.uid]);

  useEffect(() => {
    setGroupAlreadyAdded(false);
  }, [selectedGroup]);

  const handleAddViewGroup = async () => {
    setIsLoading(true);
    try {
      let result;
      if (conservSostL1 && permissions === "r") {
        result = await addViewGroupReadOnly(tag.id, selectedGroup, user.uid);
      } else {
        result = await addViewGroup(tag.id, selectedGroup, user.uid);
      }

      if (result.code === 0) {
        handleSuccessfulAdded();
      } else if (result.code === -1) {
        setGroupAlreadyAdded(true);
      } else {
        handleErrorAdded();
      }
    } catch (error) {
      handleErrorAdded();
    } finally {
      handleReset();
      setIsLoading(false);
    }
  };

  const handleChangeGroup = (e) => setSelectedGroup(e.target.value);
  const handleChangePermissions = (e) => setPermissions(e.target.value);

  const handleReset = () => {
    setSelectedGroup("");
    setPermissions("r");
    setOpen(false);
  };

  const innerContent = groups && (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl
          fullWidth
          required
          margin="dense"
          sx={{
            mt: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
            },
          }}
        >
          <InputLabel>{t("name")}</InputLabel>
          <Select label={t("name")} value={selectedGroup} onChange={handleChangeGroup}>
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {conservSostL1 && (
        <Grid item xs={12}>
          <FormControl
            fullWidth
            required
            margin="dense"
            sx={{
              mt: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: "#ffffff",
              },
            }}
          >
            <InputLabel>{t("permissions")}</InputLabel>
            <Select label={t("permissions")} value={permissions} onChange={handleChangePermissions}>
              <MenuItem value="r">Read</MenuItem>
              <MenuItem value="r/w">Read / Write</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12}>
        {groupAlreadyAdded && (
          <Typography variant="body2" color="error">
            {t("group_already_added")}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid item container justifyContent="center" mt="30%">
        <CircularProgress />
      </Grid>
    );
  }

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleReset}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
          {t("add_group")}
        </Typography>

        {groups ? (
          <>
            {innerContent}

            <Grid container spacing={2} mt={5}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                  onClick={handleAddViewGroup}
                  disabled={!selectedGroup}
                >
                  {t("add")}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="text"
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                  onClick={handleReset}
                >
                  {t("cancel")}
                </Button>
              </Grid>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Typography align="center" color="gray" variant="h6">
              {t("no_group_available")}
            </Typography>
          </Grid>
        )}
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("add_group")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={handleReset} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        {groups ? (
          innerContent
        ) : (
          <Grid item xs={12}>
            <Typography align="center" color="gray" variant="h6">
              {t("no_group_available")}
            </Typography>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleReset}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleAddViewGroup}
          disabled={!selectedGroup}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddViewGroupDialog;
